import * as React from 'react';

import { AutocompleteProps } from '@lightspeed/flame/Autocomplete';
import { IconAdd } from '@lightspeed/flame/Icon/Add';
import { Text } from '@lightspeed/flame/Text';
import cn from 'classnames';

import {
  StyledAsync,
  StyledAsyncCreatable,
  StyledAutocomplete,
  StyledCreatable,
} from './Autocomplete.styles';
import { getIsViewPage } from './utils';

const MAX_INPUT_LENGTH = 255;
const TOP_BAR_HEIGHT = 66;
const isTouch = 'ontouchstart' in document.documentElement;

const Autocomplete: React.FC<AutocompleteProps> = ({
  className = '',
  isCreatable = false,
  isAsync = false,
  onChange: parentOnChange = (_arg: any) => {},
  onInputChange: parentInputChange = (_arg: any) => {},
  formatOptionLabel,
  ...rest
}) => {
  let Component = StyledAutocomplete;
  if (isAsync) {
    if (isCreatable) {
      Component = StyledAsyncCreatable;
    } else {
      Component = StyledAsync;
    }
  } else if (isCreatable) {
    Component = StyledCreatable;
  }

  const isViewPage = getIsViewPage();

  const classNames = cn('cr-autocomplete', className, {
    'is-not-clearable': !rest.isClearable,
  });

  const defaultCreatableProps = isCreatable
    ? {
        formatCreateLabel: (inputValue: string) => (
          <Text color="blue-500" fontWeight="bold">
            <IconAdd color="blue-500" className="cr-mr-1" /> Add{' '}
            <var>&ldquo;{inputValue}&rdquo;</var>
          </Text>
        ),
      }
    : {};

  const defaultProps = {
    formatOptionLabel: (option: any) => {
      const { label, translate, __isNew__ } =
        typeof formatOptionLabel === 'function' ? formatOptionLabel(option) : option;

      return translate || __isNew__ ? label : <var>{label}</var>;
    },
    ...defaultCreatableProps,
  };

  return (
    <Component
      className={classNames}
      classNamePrefix="cr-autocomplete"
      hasError
      {...rest}
      {...defaultProps}
      onChange={(newValue: any) => {
        parentOnChange(newValue);
        if (isViewPage && !rest.preventViewFieldChanged) window.viewFieldChanged('view');
      }}
      onInputChange={(inputValue: any) => {
        const returnedValue =
          inputValue.length <= MAX_INPUT_LENGTH
            ? inputValue
            : inputValue.substr(0, MAX_INPUT_LENGTH);
        parentInputChange(returnedValue);
        return returnedValue;
      }}
      {...(isTouch
        ? {
            onFocus: (e: any) => {
              // On touch devices, we want to scroll to the top of the input when focusing,
              // else in most cases the keyboard will overlay the autocomplete menu.
              const elementPosition = e.target.getBoundingClientRect();
              window.scroll({
                top: elementPosition.top + window.scrollY - TOP_BAR_HEIGHT,
              });
            },
          }
        : {})}
    />
  );
};
export default Autocomplete;
